import { useEffect, useRef, useState } from 'react';

export interface CopyButtonProps {
  /** Text placed on the clipboard when the button is clicked. */
  text: string;
  /** Idle button label. Defaults to "Copy". */
  label?: string;
  /** Transient confirmation label. Defaults to "Copied". */
  copiedLabel?: string;
  disabled?: boolean;
  className?: string;
}

type CopyFeedback = 'idle' | 'copied' | 'failed';

/** How long the copied/failed feedback stays visible, in milliseconds. */
const FEEDBACK_MS = 1600;

/**
 * Best-effort clipboard write: async Clipboard API in secure contexts, with
 * the hidden-textarea `execCommand('copy')` fallback for older browsers and
 * non-secure origins. Resolves false instead of throwing so the button can
 * surface a failure state.
 */
async function writeClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && typeof window !== 'undefined' && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied or unavailable — fall through to the legacy path.
    }
  }
  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
}

/**
 * Client-only clipboard copy button (spec §5 Page 2: "Clipboard copy … [is a]
 * client-only utilit[y]"). Shows transient copied/failed feedback and restores
 * its idle label; never leaks the copied text into the DOM beyond the button.
 */
export default function CopyButton({
  text,
  label = 'Copy',
  copiedLabel = 'Copied',
  disabled = false,
  className,
}: CopyButtonProps) {
  const [feedback, setFeedback] = useState<CopyFeedback>('idle');
  // window.setTimeout returns a number handle; undefined means "no pending reset".
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const onClick = async () => {
    const ok = await writeClipboard(text);
    setFeedback(ok ? 'copied' : 'failed');
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setFeedback('idle'), FEEDBACK_MS);
  };

  const classes = ['copy-button', `copy-button--${feedback}`, className].filter(Boolean).join(' ');
  const visibleLabel = feedback === 'copied' ? copiedLabel : feedback === 'failed' ? 'Copy failed' : label;

  return (
    <button
      type="button"
      className={classes}
      onClick={() => {
        void onClick();
      }}
      disabled={disabled || text === ''}
      aria-live="polite"
    >
      {visibleLabel}
    </button>
  );
}
